import { FieldValues } from 'react-hook-form';
import { OptionsDrawerListProps } from './props';
import { useInfiniteScroll } from '@starsoft/common/hooks';
import { filterOptions } from '../../utils';
import { useMemo } from 'react';
import styles from './styles.module.scss';
import ListItem from './Item';
import EmptySearch from '../../../Empty';

/**
 * Component that renders a scrollable list of options with infinite loading support
 *
 * @template T - Type extending FieldValues from react-hook-form
 * @template V - Type of the option value
 *
 * @param {Object} props - Component props
 * @param {Function} props.filterBy - Function to filter options based on search text
 * @param {Array<T>} props.options - Array of available options to display
 * @param {boolean} props.hasNextPage - Whether there are more options to load
 * @param {Function} props.loadMore - Function to load additional options
 * @param {boolean} props.loading - Whether initial options are loading
 * @param {boolean} props.loadingMore - Whether additional options are loading
 * @param {string} props.search - Current search input value
 * @param {Function} props.getOptionLabel - Function to get display label from option
 * @param {Function} props.getOptionValue - Function to get value from option
 * @param {Function} props.setValue - Function to set selected value
 * @param {Object} props.customComponents - Custom component overrides
 * @param {Function} props.setOpen - Function to control options drawer visibility
 * @param {boolean} props.isAsync - Whether options are loaded asynchronously
 * @param {boolean} props.useGridOptions - Whether to display options in grid layout
 * @param {V} props.value - Currently selected value
 * @param {Function} props.onSearchChange - Callback when search value changes
 * @returns {JSX.Element} List component with options and loading states
 */
export default function OptionsDrawerList<T extends FieldValues, V>({
  filterBy,
  options,
  hasNextPage = false,
  loadMore = () => {},
  loading = false,
  loadingMore = false,
  search,
  getOptionLabel,
  getOptionValue,
  setValue,
  customComponents,
  setOpen,
  isAsync,
  useGridOptions,
  value,
  onSearchChange,
}: OptionsDrawerListProps<T, V>) {
  // Hook to handle infinite scrolling functionality
  const { ref } = useInfiniteScroll({
    hasMore: hasNextPage,
    isLoading: loadingMore || loading,
    loadMore,
    condition: isAsync,
  });

  // Filter options based on search text if not using async search
  const filteredOptions = useMemo(
    () =>
      search && !onSearchChange
        ? filterOptions(options, search, filterBy)
        : options,
    [filterBy, options, search, onSearchChange],
  );

  // Memoized list of option items
  const Items: JSX.Element[] = useMemo(
    () =>
      filteredOptions.map((option, index) => (
        <ListItem
          key={`autocomplete-menu-selector-option-${index}`}
          getOptionLabel={getOptionLabel}
          getOptionValue={getOptionValue}
          option={option}
          setOpen={setOpen}
          setValue={setValue}
          customComponents={customComponents}
          value={value}
        />
      )),
    [
      customComponents,
      filteredOptions,
      getOptionLabel,
      getOptionValue,
      setOpen,
      setValue,
      value,
    ],
  );

  // Memoized list of skeleton loading items
  const Skeletons: JSX.Element[] = useMemo(
    () =>
      Array.from({ length: 15 }).map((_, index) => {
        return (
          <ListItem
            key={`autocomplete-skeleton-menu-selector-option-${index}`}
            getOptionLabel={getOptionLabel}
            getOptionValue={getOptionValue}
            loading
            setOpen={setOpen}
            setValue={setValue}
            customComponents={customComponents}
            value={value}
          />
        );
      }),
    [
      customComponents,
      getOptionLabel,
      getOptionValue,
      setOpen,
      setValue,
      value,
    ],
  );

  // Show empty state if no options and not loading
  if (filteredOptions.length === 0 && !loading && !loadingMore) {
    return (
      <div className={styles.container}>
        <EmptySearch search={search} />
      </div>
    );
  }

  // Render list of options with optional grid layout
  return (
    <div
      className={`${styles.container} ${useGridOptions ? styles['container--grid'] : ''}`}
      ref={ref}
    >
      {Items}
      {(loading || loadingMore) && isAsync && Skeletons}
    </div>
  );
}
